import { STATUS } from "@/constants/statuses";
import type { MemoryHistoryInterface } from "@apps/rewards/lib/MemoryHistory/MemoryHistoryInterface";
import type { Observable } from "rxjs";
import { combineLatest, map } from "rxjs";

import type { RewardsSpendingRulesEntity } from "../models/RewardsSpendingRulesEntities";
import type { RewardsSpendingRulesPointsParams } from "../types";

/**
 * @description This function returns the minimum spendable points for a given spending rule.
 *
 * @param {RewardsSpendingRulesEntity} rule - The spending rule object.
 * @returns  {number} The minimum spendable points for the rule.
 */
export const getMinSpendablePoints = (
    rule: RewardsSpendingRulesEntity["rule"]
): number | null =>
    "minSpendablePoints" in rule ? rule.minSpendablePoints : null;

/**
 * @description Check if the user can redeem points based on their balance and the spending rules settings.
 *
 * @param {RewardsSpendingRulesPointsParams} params - The parameters for the function.
 * @returns {boolean} Returns true if the user can redeem, false otherwise.
 */
export const canRedeem = ({
    pointsPrice,
    userBalance = 0,
    minSpendablePoints,
}: RewardsSpendingRulesPointsParams): boolean =>
    userBalance >= (minSpendablePoints || pointsPrice);

/**
 * @description Calculate the missing points needed for a user to redeem based on their balance and the spending rules settings.
 *
 * @param {RewardsSpendingRulesPointsParams} params - The parameters for the function.
 * @returns {number} Returns the number of points needed to redeem.
 */
export const getMissingPoints = ({
    pointsPrice,
    userBalance = 0,
    minSpendablePoints,
}: RewardsSpendingRulesPointsParams): number =>
    (minSpendablePoints || pointsPrice) - userBalance;

/**
 * @description Check if the current view is the same as the given path.
 *
 * @param {string} currentPath - The path to check against.
 * @param {MemoryHistoryInterface} history - The history object to check the current location.
 * @returns {boolean} Returns true if the current view matches the given path, false otherwise.
 */
export const isCurrentView = (
    currentPath: string,
    history: MemoryHistoryInterface
): boolean => history.location.pathname === currentPath;

/**
 * @description Get the loading status of the redeem process.
 *
 * @returns {boolean} Returns true if the redeem process is loading, false otherwise.
 */
export const getRedeemLoadingStatus = (
    activeRewardsRequestStatus: STATUS,
    isRedeemLoading: boolean
): boolean =>
    activeRewardsRequestStatus === STATUS.LOADING ||
    activeRewardsRequestStatus === STATUS.INITIAL_LOADING ||
    isRedeemLoading;

/**
 * @description Filters the spending rules list based on whether the user is a customer and rule has customer whitelist tags.
 *
 * @param {Observable<RewardsSpendingRulesEntity[]>} spendingRulesList$
 * @param {Observable<boolean>} isCustomer$
 * @returns {Observable<RewardsSpendingRulesEntity[]>} Filtered list of spending rules.
 */
export const selectFilteredSpendingRulesList = (
    spendingRulesList$: Observable<RewardsSpendingRulesEntity[]>,
    isCustomer$: Observable<boolean>
): Observable<RewardsSpendingRulesEntity[]> =>
    combineLatest([spendingRulesList$, isCustomer$]).pipe(
        map(([list, isCustomer]) =>
            isCustomer
                ? list
                : list.filter(
                      (item) => !item.rule.customerTags.whitelist.length
                  )
        )
    );
